import { c as _c } from "react/compiler-runtime";
import { isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useField } from 'formik';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import NumberFormat from 'react-number-format';
import { usePopper } from 'react-popper';
import styled, { css, keyframes } from 'styled-components';
import { Resolution } from '../../../utils/chart';
import { formatDate } from '../../../utils/date';
import { useDatePreferences } from '../../hooks/useDatePreferences';
import { StyledCard, StyledCardSection } from '../base/Card';
import Divider from '../base/Divider';
import Text from '../base/Text';
import { Div } from '../helpers/StyledUtils';
export interface DimensionConfig {
  value: string | null;
  color: string;
  label: string;
}
export interface HistogramBarData {
  start: Date;
  end: Date;
  values: Record<string, number>;
  total: number;
}
interface HistogramBarChartProps {
  bars: HistogramBarData[] | null;
  resolution: Resolution;
  dimension_configs?: DimensionConfig[];
  tooltip_label?: string;
  empty_state?: React.ReactNode;
}
const placeholder_data = Array.from(Array(60).keys()).map(() => Math.random() * 100);
const StyledChartContainer = styled.div<{
  empty?: boolean;
}>(({
  theme,
  empty
}) => css`
    height: ${empty ? '62px' : '32px'};
    display: grid;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
    width: 100%;
    margin-bottom: ${theme.spacing(0)};
  `);
const fade = keyframes`
  from {
    opacity: 0.25;
  }
`;
const StyledChartValue = styled.div<{
  color?: string;
  $loading?: boolean;
  max_value: number;
  value: number;
}>(({
  theme,
  color,
  max_value,
  value,
  $loading
}) => css`
    height: ${(value / max_value * 100).toFixed(1)}%;
    min-height: 2px;
    background-color: ${value === 0 || !color ? theme.colors.surface.container.neutral : theme.colors.surface.base[color]};
    border-radius: 1px;
    width: 100%;
    margin: 0.5px 0px;
    align-self: end;
    ${$loading && css`
      opacity: 0.6 !important;
      background-color: ${theme.colors.surface.container.neutral};
      animation: ${fade} 1s infinite alternate;
    `}
  `);
const StyledChartEntry = styled.div<{
  low_opacity?: boolean;
  hovered?: boolean;
  isLast?: boolean;
}>(({
  low_opacity,
  hovered,
  theme,
  isLast
}) => css`
    display: flex;
    flex-direction: column-reverse;
    position: relative;
    z-index: 1;
    padding: 0 1px;
    cursor: ew-resize;

    &:first-of-type {
      padding-left: 0;
    }

    // We can't use :last-of-type because we add StyledChartTooltipWrapper after the last child when is hovered
    ${isLast && css`
      padding-right: 0;
    `}
    ${low_opacity && !hovered && css`
      ${StyledChartValue} {
        opacity: 0.4;
        transition: opacity 0.15s ease-in;
      }
    `}
    ${hovered && css`
      background-color: ${theme.colors.surface.base.hover.neutral};
      border-radius: 0;
      z-index: 10;
    `}
  `);
const StyledChartTooltipWrapper = styled.div`
  width: fit-content;
  z-index: 2000;
`;
const StyledChartTooltip = styled(StyledCard)<{
  timerange_selected: boolean;
}>(({
  theme,
  timerange_selected
}) => css`
    box-shadow: ${theme.elevation[1]};
    border: none;
    margin-top: ${timerange_selected ? '54px' : '8px'};
  `);
const granularity_formating = {
  second: 'MMM d, h:mm:ss a',
  minute: 'MMM d, h:mm a',
  hour: 'MMM d, h:mm a',
  day: 'MMM d, h:mm a'
};
const getTooltipTimeLabel = (start: Date, end: Date, resolution: Resolution, timezone: string) => {
  const format_string = granularity_formating[resolution.granularity];
  const start_label = formatDate(start, format_string, timezone);
  const end_label = formatDate(end, isSameDay(utcToZonedTime(start, timezone), utcToZonedTime(end, timezone)) ? format_string.replace('MMM d,', '') : format_string, timezone);
  return `${start_label} - ${end_label}`;
};
const HistogramBarChart: React.FC<HistogramBarChartProps> = t0 => {
  const $ = _c(32);
  const {
    bars,
    resolution,
    dimension_configs,
    tooltip_label,
    empty_state
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [t1] = useField("timebar_start");
  const {
    value: timebar_start
  } = t1;
  const [t2] = useField("timebar_end");
  const {
    value: timebar_end
  } = t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = document.querySelector("#main");
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const element = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      placement: "bottom",
      modifiers: [{
        name: "preventOverflow",
        options: {
          boundary: element as any,
          mainAxis: true,
          altAxis: false,
          padding: 8
        }
      }]
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    styles,
    attributes
  } = usePopper(referenceElement, popperElement, t4);
  const [hovered_date, setHoveredDate] = useState(null);
  const timerange_selected = timebar_start !== 0 || timebar_end !== resolution.steps - 1;
  let t5;
  bb0: {
    if (!bars) {
      t5 = 0;
      break bb0;
    }
    let t6;
    if ($[2] !== bars) {
      t6 = bars.reduce(_temp, 0);
      $[2] = bars;
      $[3] = t6;
    } else {
      t6 = $[3];
    }
    t5 = t6;
  }
  const max_value = t5;
  let t6;
  if ($[4] !== bars || $[5] !== hovered_date) {
    t6 = hovered_date && bars?.find(v => hovered_date.getTime() === v.start.getTime());
    $[4] = bars;
    $[5] = hovered_date;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const displayed_hovered_date = t6;
  const has_empty_state = empty_state && !bars;
  const t7 = !!has_empty_state;
  const t8 = has_empty_state && empty_state;
  let t9;
  if ($[7] !== bars || $[8] !== empty_state) {
    t9 = !bars && !empty_state && placeholder_data.map(_temp2);
    $[7] = bars;
    $[8] = empty_state;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== bars || $[11] !== dimension_configs || $[12] !== hovered_date || $[13] !== max_value || $[14] !== timebar_end || $[15] !== timebar_start) {
    t10 = bars?.map((t11, i_0) => {
      const {
        start,
        values,
        total
      } = t11;
      const hovered = hovered_date && hovered_date.getTime() === start.getTime();
      return <StyledChartEntry low_opacity={!!(hovered_date && !hovered) || timebar_start > i_0 || timebar_end < i_0} hovered={!!hovered} key={start.getTime()} onMouseEnter={() => setHoveredDate(start)} onMouseLeave={() => setHoveredDate(null)} isLast={i_0 === bars.length - 1}>{hovered && <div ref={setReferenceElement} />}{dimension_configs ? dimension_configs.map(config => (values[config.value as string] || 0) > 0 && <StyledChartValue key={config.value} color={config.color} max_value={max_value} value={values[config.value as string]} />) : total > 0 && <StyledChartValue color="green_histogram" max_value={max_value} value={total} />}</StyledChartEntry>;
    });
    $[10] = bars;
    $[11] = dimension_configs;
    $[12] = hovered_date;
    $[13] = max_value;
    $[14] = timebar_end;
    $[15] = timebar_start;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== attributes || $[18] !== dimension_configs || $[19] !== displayed_hovered_date || $[20] !== resolution || $[21] !== styles || $[22] !== timerange_selected || $[23] !== timezone || $[24] !== tooltip_label) {
    t11 = displayed_hovered_date && createPortal(<StyledChartTooltipWrapper ref={setPopperElement} style={styles.popper} {...attributes.popper}><StyledChartTooltip timerange_selected={timerange_selected} overflow_hidden={true}><StyledCardSection p={{
          x: 3,
          y: 2
        }}><Text text_wrap={false} semi={true} as="span">{getTooltipTimeLabel(displayed_hovered_date.start, displayed_hovered_date.end, resolution, timezone)}</Text></StyledCardSection><StyledCardSection p={{
          x: 3,
          y: 2
        }}>{displayed_hovered_date.total > 0 ? dimension_configs ? <>{dimension_configs.filter(config_0 => !!displayed_hovered_date.values[config_0.value as string]).map(config_1 => <Div key={config_1.value} m={{
              b: 1
            }} flex={{
              justify: "space-between"
            }}><Text semi={true} muted={true}>{config_1.label}</Text><Text semi={true} muted={true} m={{
                l: 3
              }}><NumberFormat renderText={_temp3} displayType="text" value={displayed_hovered_date.values[config_1.value as string] || 0} thousandSeparator="," /></Text></Div>)}<Divider m={{
              y: 2
            }} /><Div flex={{
              justify: "space-between"
            }}><Text semi={true}>Total</Text><Text semi={true}><NumberFormat renderText={_temp4} displayType="text" value={displayed_hovered_date.total} thousandSeparator="," /></Text></Div></> : <Div flex={{
            justify: "space-between"
          }}><Text semi={true}>{tooltip_label || "Events"}</Text><Text semi={true} m={{
              l: 3
            }}><NumberFormat renderText={_temp5} displayType="text" value={displayed_hovered_date.total} thousandSeparator="," /></Text></Div> : <Text muted={true}>No {tooltip_label || "Events"}</Text>}</StyledCardSection></StyledChartTooltip></StyledChartTooltipWrapper>, document.body);
    $[17] = attributes;
    $[18] = dimension_configs;
    $[19] = displayed_hovered_date;
    $[20] = resolution;
    $[21] = styles;
    $[22] = timerange_selected;
    $[23] = timezone;
    $[24] = tooltip_label;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t12 = <StyledChartContainer empty={t7}>{t8}{t9}{t10}{t11}</StyledChartContainer>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
export default HistogramBarChart;
function _temp(max, bar) {
  return Math.max(max, bar.total);
}
function _temp2(v_0, i) {
  return <StyledChartEntry key={i} isLast={i === placeholder_data.length - 1}><StyledChartValue $loading={true} max_value={100} value={v_0} /></StyledChartEntry>;
}
function _temp3(v_1) {
  return v_1;
}
function _temp4(v_2) {
  return v_2;
}
function _temp5(v_3) {
  return v_3;
}